import React, { useState } from "react";
import Meta from "./../components/Meta";
import FormAlert from "./../components/FormAlert";
import ReservationForm from "./../components/ReservationForm";
import { useAuth } from "./../util/auth";
import { useRouter } from "./../util/router";
import { getAvailableTables, createReservation, getReservationsForDate } from "./../util/db";
import { Link } from "react-router-dom";

function IndexPage() {
  const auth = useAuth();
  const router = useRouter();
  const [formAlert, setFormAlert] = useState(null);
  const [pending, setPending] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  // Helper function to format date to a readable string
  const formatDate = (dateString) => {
    const [year, month, day] = dateString.split('-');
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to format time from 24-hour format to 12-hour format
  const formatTime = (timeString) => {
    const [hours, minutes] = timeString.split(':').map(Number);
    const period = hours >= 12 ? 'PM' : 'AM';
    const displayHours = hours % 12 || 12;
    return `${displayHours}:${minutes.toString().padStart(2, '0')} ${period}`;
  };

  // Redirect to sign in page if user is not authenticated
  React.useEffect(() => {
    // Only redirect if auth has finished initializing and user is not authenticated
    // Don't redirect if auth is still loading (auth.user === null)
    if (auth.user === false) {
      router.push("/auth/signin?next=/&message=Please sign in to make a reservation at Sushi Edomata");
    }
  }, [auth.user, router]);

  // Handle form submission
  const onSubmit = async (data) => {
    // Show pending indicator immediately to prevent double-clicks
    setPending(true);
    
    try {
      // Check if the user is an admin
      const isAdmin = auth.user && auth.user.email === 'sanmateosushi.reservations@gmail.com';
      
      // Convert selected time to military format (e.g., "19:00" to 1900)
      const [hours, minutes] = data.time.split(":");
      const startTime = parseInt(hours + minutes);
      
      // Calculate end time properly handling minutes (90-minute reservation)
      const totalMinutes = (hours * 60 + parseInt(minutes)) + 90;
      const endHours = Math.floor(totalMinutes / 60);
      const endMinutes = totalMinutes % 60;
      const endTime = (endHours * 100) + endMinutes;
      
      // Get available tables using the optimized function
      let availableTables;
      try {
        availableTables = await getAvailableTables(
          data.date,
          startTime,
          endTime,
          parseInt(data.partySize),
          null,
          data.seatType,
          isAdmin // Pass isAdmin flag to bypass isReservable check if admin
        );
      } catch (error) {
        console.error("Error getting available tables:", error);
        setFormAlert({
          type: "error",
          message: `Error checking table availability: ${error.message}`
        });
        setPending(false);
        return;
      }

      if (availableTables.length === 0) {
        setFormAlert({
          type: "error",
          message: "That time slot has been booked. Please refresh the page."
        });
        setPending(false);
        return;
      }

      // IMPORTANT: Re-check availability just before creating the reservation
      // This prevents race conditions where multiple users book the same slot
      let finalAvailableTables;
      try {
        finalAvailableTables = await getAvailableTables(
          data.date,
          startTime,
          endTime,
          parseInt(data.partySize),
          null,
          data.seatType,
          isAdmin
        );
      } catch (error) {
        console.error("Error in final availability check:", error);
        setFormAlert({
          type: "error",
          message: `Error checking table availability: ${error.message}`
        });
        setPending(false);
        return;
      }

      // Check if we can still accommodate the party size
      // (The algorithm might return different table combinations, but we just need to ensure availability)
      if (finalAvailableTables.length === 0) {
        setFormAlert({
          type: "error",
          message: "That time slot has been booked. Please refresh the page."
        });
        setPending(false);
        return;
      }

      // Use the tables from the final availability check to ensure they're still available
      let finalTableIds;
      if (data.tableIds && data.tableIds.length > 0) {
        // Admin has selected specific tables - check if they're still available
        // We need to check this differently than the optimization algorithm
        // because the admin chose specific tables, not optimal ones
        try {
          const reservations = await getReservationsForDate(data.date);
          
          // Check if any of the selected tables have conflicting reservations
          const hasConflict = data.tableIds.some(tableId => {
            const tableReservations = reservations.filter(r => r.tableIds?.includes(tableId));
            return tableReservations.some(existing => {
              return startTime < existing.endTime && endTime > existing.startTime;
            });
          });
          
          if (hasConflict) {
            setFormAlert({
              type: "error",
              message: "That time slot has been booked. Please refresh the page."
            });
            setPending(false);
            return;
          }
          
          finalTableIds = data.tableIds;
        } catch (error) {
          console.error("Error checking table conflicts:", error);
          setFormAlert({
            type: "error",
            message: `Error checking table availability: ${error.message}`
          });
          setPending(false);
          return;
        }
      } else {
        // No specific tables selected - use whatever is available
        finalTableIds = finalAvailableTables.map(table => table.id);
      }

      // Create reservation with the appropriate table(s)
      const reservation = await createReservation({
        tableIds: finalTableIds,
        startTime,
        endTime,
        reservationDate: data.date,
        email: auth.user.email,
        reminderEmail: data.reminderEmail,
        partySize: parseInt(data.partySize),
        name: data.name,
        phoneNumber: data.phoneNumber,
        seatType: data.seatType
      });

      // Fire-and-forget: trigger confirmation email via the reminders API
      if (data.reminderEmail) {
        const apiUrl = process.env.REACT_APP_API_URL;
        console.log('Sending confirmation email, API URL:', apiUrl, 'Reservation ID:', reservation.id);
        fetch(`${apiUrl}/sendReservationConfirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ reservationId: reservation.id })
        })
          .then(resp => console.log('Confirmation email response:', resp.status, resp.statusText))
          .catch(err => console.error('Failed to send confirmation email:', err));
      }

      setFormAlert({
        type: "success",
        message: (
          <div className="space-y-2">
            <p className="font-medium">Your reservation has been confirmed!</p>
            <p>Date: {formatDate(data.date)}</p>
            <p>Time: {formatTime(data.time)} (1.5 hours)</p>
            <p>Party Size: {data.partySize} {data.partySize === 1 ? 'person' : 'people'}</p>
            <p className="text-sm text-gray-600">A confirmation email has been sent to {data.reminderEmail}. You'll also receive a reminder before your reservation.</p>
            <p className="text-sm text-gray-600">
              Please call us at <a href="tel:650-344-0888" className="text-blue-600 hover:text-blue-800">650-344-0888</a> in advance if you have any special requests or if you need to modify or cancel your reservation. 
              You can also see your reservation in <a href="/my-reservations" className="text-blue-600 hover:text-blue-800">My Reservations</a> and modify or cancel there.
            </p>
            <p className="text-sm text-gray-600 mt-4 pt-2 border-t border-gray-200">
              <strong>To make another reservation, please refresh this page.</strong>
            </p>
          </div>
        )
      });

      // Mark as submitted to hide the form
      setIsSubmitted(true);

      // Scroll to the top of the component
      const container = document.querySelector('.container');
      if (container) {
        container.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch (error) {
      console.error("Reservation creation error:", error);
      setFormAlert({
        type: "error",
        message: error.message || "An error occurred while creating your reservation. Please try again."
      });
    } finally {
      setPending(false);
    }
  };

  // Show loading indicator if auth is initializing
  if (auth.user === null) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <>
      <Meta title="Sushi Edomata - Reservations" />
      <section className="py-8 sm:py-12 bg-blue-50">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="max-w-md mx-auto bg-white rounded-lg shadow-md p-4 sm:p-6">
            <h1 className="text-2xl sm:text-3xl font-bold text-center mb-4 sm:mb-6">Make a Reservation</h1>
            
            {auth.user && (
              <>
                {auth.user.email === 'sanmateosushi.reservations@gmail.com' && (
                  <div className="mb-4 bg-green-50 text-green-700 p-3 rounded-md border border-green-200">
                    <p className="text-sm font-medium">Admin mode: You can book non-reservable tables.</p>
                  </div>
                )}
                
                {formAlert && (
                  <div className="mb-4 sm:mb-6">
                    <FormAlert type={formAlert.type} message={formAlert.message} />
                  </div>
                )}
                
                {/* Only show the form if not successfully submitted */}
                {!isSubmitted && (
                  <ReservationForm
                    onSubmit={onSubmit}
                    initialValues={{}}
                    submitButtonText="Make Reservation"
                    submitButtonDisabled={pending}
                    onCancel={() => {}}
                    showCancelButton={false}
                    className="max-w-2xl mx-auto"
                    isAdmin={auth.user && auth.user.email === 'sanmateosushi.reservations@gmail.com'}
                  />
                )}
              </>
            )}
          </div>
          
          {/* Privacy Policy Link */}
          <div className="max-w-md mx-auto mt-4 text-center">
            <p className="text-xs text-gray-600">
              We only use your email address to send a reminder on the morning of your reservation.
              See our <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link> for details.
            </p>
          </div>
        </div>
      </section>
    </>
  );
}

export default IndexPage;
